/* eslint-disable */
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import ResourceTreeView from './ResourceTreeView'
import { createUser, editManagePermission, uploadUserInfo } from '../../apiCalls/permissionApi'
import ErrorSnackAlert from '../../components/PlatformLayout/ErrorSnackAlert'
import { useApiPost } from '../../apiCalls/useApi'
import UserInfo from './UserInfo'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { createUserPasswordSchema } from '../../components/PasswordField/validation'

interface Permission {
  scope: string
  scope_id: string
  scope_name?: string
  role: string
}

interface User {
  id: string
  email: string
  firstname: string
  lastname: string
  phonenumber: string
  permission: Permission[]
}

interface CreateUserDialogProps {
  open: boolean
  setOpen: (open: boolean) => void
  onClose: () => void
  setSaveClickUpdate: React.Dispatch<React.SetStateAction<number>>
}

const schema = yup.object().shape({
  firstname: yup
    .string()
    .max(50, 'First name must be at most 50 characters')
    .required('First name is required'),
  lastname: yup
    .string()
    .max(50, 'Last name must be at most 50 characters')
    .required('Last name is required'),
  email: yup
    .string()
    .email('Invalid email address')
    .max(254, 'Email must be at most 254 characters')
    .required('Email is required'),
  phonenumber: yup
    .string()
    .notRequired()
    .max(15, 'Phone number must be at most 15 characters')
    .test('is-valid-phone', 'Phone number must be a valid US or Canadian number', function (value) {
      if (!value) return true
      const phoneRegex = /^(\+?1)?[ -.]?(\(?[2-9]\d{2}\)?)[ -.]?\d{3}[ -.]?\d{4}$/
      return phoneRegex.test(value)
    }),
  password: createUserPasswordSchema(),
})

export default function CreateUserDialog({
  open,
  setOpen,
  onClose,
  setSaveClickUpdate,
}: CreateUserDialogProps) {
  const [currentUser, setCurrentUser] = useState<User>({
    id: '',
    email: '',
    firstname: '',
    lastname: '',
    phonenumber: '',
    permission: [],
  })
  const [permissionsObjectToApiRequest, setPermissionsObjectToApiRequest] = useState<any>(null)
  const [isErrorAlertOpen, setIsErrorAlertOpen] = useState(false)
  const [resetFlag, setResetFlag] = useState<number>(0)
  const createNewUser = useApiPost(createUser)
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      firstname: '',
      lastname: '',
      email: '',
      phonenumber: '',
      password: '',
    },
    resolver: yupResolver(schema),
  })

  const handleCloseErrorAlert = () => {
    setIsErrorAlertOpen(false)
  }

  const saveOnClick = handleSubmit(async (userInfo: any) => {
    if (!permissionsObjectToApiRequest || permissionsObjectToApiRequest.permissions.length === 0) {
      setIsErrorAlertOpen(true)
      return
    }
    const createUserError = await createNewUser({
      ...userInfo,
      permissions: permissionsObjectToApiRequest.permissions,
    })
    if (createUserError) return
    setSaveClickUpdate((prev) => prev + 1)
    setOpen(false)
  })

  return (
    <Dialog
      open={open}
      onClose={() => {
        onClose()
      }}
      PaperProps={{
        style: {
          width: '90vw',
          maxWidth: '90vw',
          maxHeight: '85vh',
          height: '85vh',
          margin: 'auto',
          backgroundColor: '#FFFFFF',
          borderRadius: '10px',
        },
      }}
    >
      <DialogTitle>Create User</DialogTitle>
      <DialogContent>
        <Grid container spacing={4}>
          <UserInfo isEditing={true} control={control} errors={errors} />

          <div
            style={{
              display: 'flex',
              height: '60vh',
              borderLeft: '1px solid black',
              maxWidth: '1px',
              margin: '20px 16px',
            }}
          ></div>

          <Grid item xs={12} md={6}>
            <Typography variant='h6' sx={{ paddingLeft: '30px' }}>
              Assign Permissions
            </Typography>
            <ResourceTreeView
              isEditing={true}
              user={currentUser}
              setPermissionsObjectToApiRequest={setPermissionsObjectToApiRequest}
              resetFlag={resetFlag}
            />
          </Grid>
        </Grid>

        <div
          style={{ marginTop: '30px', display: 'flex', width: '94%', justifyContent: 'flex-end' }}
        >
          <Button variant='contained' color='primary' onClick={saveOnClick}>
            Create User
          </Button>
        </div>
      </DialogContent>
      <ErrorSnackAlert
        open={isErrorAlertOpen}
        handleCloseError={handleCloseErrorAlert}
        text='You cannot set an empty permission.'
      />
    </Dialog>
  )
}
